import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Select, message,
  Modal, Switch, Tooltip, Spin, InputNumber
} from 'antd';
import {
  PlusOutlined, CalendarOutlined, TagOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined,
  CrownOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined, StarFilled, RocketOutlined,
  PhoneOutlined, UserOutlined, ThunderboltOutlined,
  FilterOutlined
} from '@ant-design/icons';
import Swal from 'sweetalert2';
import {
  getPremiumPlans, 
  createPremiumPlan,
  updatePremiumPlan, 
  deletePremiumPlan,
  PostChargesPost,
  PostChargesGet,
  PostChargesUpdate,
} from '../../../../Api/CoreApi';
import './AddPremiumPlan.css';

const { Option } = Select;
const { TextArea } = Input;

const AddPremiumPlan = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [plans, setPlans] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);

  // 📱 Mobile state for responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ✅ New states
  const [selectedPlanType, setSelectedPlanType] = useState('contact_view');
  const [editPlanType, setEditPlanType] = useState('contact_view');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Plan type configurations
  const planTypes = {
    contact_view: {
      icon: '📱',
      antIcon: <PhoneOutlined />,
      label: 'Contact View',
      color: '#3b82f6',
      bg: '#eff6ff',
      border: '#bfdbfe',
      desc: 'Users can view contact details of other profiles'
    },
    specific_user: {
      icon: '👤',
      antIcon: <UserOutlined />,
      label: 'Specific User',
      color: '#8b5cf6',
      bg: '#f5f3ff',
      border: '#ddd6fe',
      desc: 'Custom plan for specific user'
    },
    profile_boost: {
      icon: '🚀',
      antIcon: <ThunderboltOutlined />,
      label: 'Profile Boost',
      color: '#f59e0b',
      bg: '#fffbeb',
      border: '#fde68a',
      desc: 'Boost profile to top position'
    },
    custom: {
      icon: '⚙️',
      antIcon: <CrownOutlined />,
      label: 'Custom Plan',
      color: '#10b981',
      bg: '#ecfdf5',
      border: '#a7f3d0',
      desc: 'Custom plan with specific features'
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const data = await getPremiumPlans();
      setPlans(data || []);
    } catch (error) {
      message.error("Failed to load plans");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPlans();
    setRefreshing(false);
    message.success("Refreshed");
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        price: parseInt(values.price) || 0,
        contact_view_limit: parseInt(values.contact_view_limit) || 0,
        profile_view_limit: parseInt(values.profile_view_limit) || 0,
        plan_type: selectedPlanType,
      };
      
      // 1. Create Plan
      const createdPlan = await createPremiumPlan(payload);
      
      // 2. Auto Create Commission Structure
      try {
        await PostChargesPost({
          title: `${payload.plan_name} - Commission`,
          plan_id: createdPlan?.id,
          plan_name: payload.plan_name,
          plan_price: payload.price,
          plan_type: payload.plan_type,
          admin_percentage: 70,
          staff_percentage: 15,
          developer_percentage: 15,
          self_admin_percentage: 85,
          self_staff_percentage: 0,
          self_developer_percentage: 15,
          description: `Auto-generated commission for ${payload.plan_name}.`,
          is_active: true,
          is_auto_generated: true,
        });
      } catch (commErr) {
        console.warn("⚠️ Commission auto-create failed:", commErr);
      }
      
      await fetchPlans();
      message.success("Plan added successfully!");
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to add plan");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingPlan(record);
    setEditPlanType(record.plan_type || 'contact_view');
    editForm.setFieldsValue({
      plan_name: record.plan_name,
      plan_type: record.plan_type,
      price: record.price,
      duration: record.duration,
      features: record.features,
      status: record.status,
      is_popular: record.is_popular,
      contact_view_limit: record.contact_view_limit,
      profile_view_limit: record.profile_view_limit,
      boost_position: record.boost_position,
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        price: parseInt(values.price) || 0,
        contact_view_limit: parseInt(values.contact_view_limit) || 0,
        profile_view_limit: parseInt(values.profile_view_limit) || 0,
        plan_type: editPlanType,
      };

      await updatePremiumPlan(editingPlan.id, payload);

      try {
        const allCommissions = await PostChargesGet();
        const linkedCommission = allCommissions?.find(
          c => c.plan_id === editingPlan.id && c.is_auto_generated === true
        );

        if (linkedCommission) {
          const updatedCommission = {
            ...linkedCommission,
            title: `${values.plan_name} - Commission`,
            description: `Auto-generated commission for plan: ${values.plan_name} | Price: ₹${parseInt(values.price) || 0}`,
            plan_name: values.plan_name,
            plan_price: parseInt(values.price) || 0,
            plan_type: editPlanType,
          };
          await PostChargesUpdate(linkedCommission.id, updatedCommission);
          message.success("Plan & Commission updated!");
        } else {
          const commissionPayload = {
            title: `${values.plan_name} - Commission`,
            description: `Auto-generated commission for plan: ${values.plan_name} | Price: ₹${parseInt(values.price) || 0}`,
            admin_percentage: 70,
            staff_percentage: 15,
            developer_percentage: 15,
            self_admin_percentage: 85,
            self_staff_percentage: 0,
            self_developer_percentage: 15,
            plan_id: editingPlan.id,
            plan_name: values.plan_name,
            plan_price: parseInt(values.price) || 0,
            plan_type: editPlanType,
            is_active: true,
            is_auto_generated: true,
          };
          await PostChargesPost(commissionPayload);
          message.success("Plan updated & Commission created!");
        }
      } catch (commissionError) {
        message.success("Plan updated! (Commission sync failed)");
      }

      await fetchPlans();
      setIsEditModalVisible(false);
      setEditingPlan(null);

    } catch (error) {
      message.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Plan?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff0055",
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePremiumPlan(id);
          await fetchPlans();
          message.success("Deleted!");
        } catch { message.error("Failed"); }
      }
    });
  };

  const handleView = (record) => {
    setSelectedPlan(record);
    setViewModalVisible(true);
  };

  const getDurationLabel = (d) => {
    const labels = {
      7: '7 Days', 15: '15 Days', 30: '1 Month',
      60: '2 Months', 90: '3 Months', 180: '6 Months', 365: '1 Year'
    };
    return labels[d] || `${d} Days`;
  };

  const getTypeConfig = (type) => planTypes[type] || planTypes.custom;

  const filteredPlans = typeFilter === 'all'
    ? plans
    : plans.filter(p => p.plan_type === typeFilter);

  const stats = {
    total: plans.length,
    contact_view: plans.filter(p => p.plan_type === 'contact_view').length,
    specific_user: plans.filter(p => p.plan_type === 'specific_user').length,
    profile_boost: plans.filter(p => p.plan_type === 'profile_boost').length,
    active: plans.filter(p => p.status === 'active').length,
  };

  const renderTypeFields = (type, isEdit = false) => {
    return (
      <>
        {type === 'contact_view' && (
          <div className="plan-type-config-box contact">
            <p className="type-config-title">📱 Contact View Settings</p>
            <Form.Item name="contact_view_limit" label="Number of Contacts User Can View" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber style={{ width: '100%' }} min={1} max={9999} placeholder="e.g. 5, 10, 50, Unlimited=9999" />
            </Form.Item>
            <p className="type-config-hint">💡 Set 9999 for unlimited contact views</p>
          </div>
        )}

        {type === 'specific_user' && (
          <div className="plan-type-config-box specific">
            <p className="type-config-title">👤 Specific User Settings</p>
            <Form.Item name="profile_view_limit" label="Number of Profiles to Show" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber style={{ width: '100%' }} min={1} max={9999} placeholder="e.g. 5, 10, 20" />
            </Form.Item>
            <p className="type-config-hint">💡 This plan can be assigned to any specific user</p>
          </div>
        )}

        {type === 'profile_boost' && (
          <div className="plan-type-config-box boost">
            <p className="type-config-title">🚀 Profile Boost Settings</p>
            <Form.Item name="boost_position" label="Boost Position" rules={[{ required: true, message: 'Required' }]}>
              <Select placeholder="Select position">
                <Option value="top">🥇 Top Position</Option>
                <Option value="featured">⭐ Featured Section</Option>
                <Option value="highlighted">✨ Highlighted</Option>
              </Select>
            </Form.Item>
            <p className="type-config-hint">💡 User's profile will appear at selected position</p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="plans-page">
      {/* ===== HEADER ===== */}
      <div className="plans-topbar">
        <div className="plans-topbar-left">
          <div className="plans-topbar-icon"><CrownOutlined /></div>
          <div className="plans-topbar-text">
            <h1>Premium Plans <span className="plans-count">{plans.length}</span></h1>
            <p className="desktop-only-text">Manage subscription plans and pricing</p>
          </div>
        </div>
        <div className="plans-topbar-actions">
          <button className="plans-btn-ghost" onClick={handleRefresh} disabled={refreshing}>
            <ReloadOutlined spin={refreshing} className="btn-icon" /> 
            <span className="btn-text">Refresh</span>
          </button>
          <button className="plans-btn-primary" onClick={() => setIsModalVisible(true)}>
            <PlusOutlined className="btn-icon" /> 
            <span className="btn-text">Add Plan</span>
          </button>
        </div>
      </div>

      {/* ===== TYPE STATS ===== */}
      <div className="plans-stats">
        {[
          { key: 'total', icon: <CrownOutlined />, label: 'Total Plans', value: stats.total },
          { key: 'contact_view', icon: <PhoneOutlined />, label: 'Contact View', value: stats.contact_view },
          { key: 'specific_user', icon: <UserOutlined />, label: 'Specific User', value: stats.specific_user },
          { key: 'profile_boost', icon: <ThunderboltOutlined />, label: 'Profile Boost', value: stats.profile_boost },
          { key: 'active', icon: <CheckCircleOutlined />, label: 'Active Plans', value: stats.active },
        ].map(s => (
          <div key={s.key} className="plans-stat">
            <div className="plans-stat-ic">{s.icon}</div>
            <div className="plans-stat-content">
              <div className="plans-stat-value">{s.value}</div>
              <div className="plans-stat-name">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== TYPE FILTER ===== */}
      <div className={isMobile ? "plans-filters-container" : "plans-filters-wrapper"}>
        <button
          onClick={() => setTypeFilter('all')}
          className={`plans-filter-btn ${typeFilter === 'all' ? 'active' : ''}`}
        >
          <FilterOutlined /> All ({stats.total})
        </button>

        {Object.entries(planTypes).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setTypeFilter(key)}
            className={`plans-filter-btn ${typeFilter === key ? 'active' : ''}`}
            style={typeFilter === key ? { background: config.bg, color: config.color, borderColor: config.color } : {}}
          >
            {config.icon} {config.label}
          </button>
        ))}
      </div>

      {/* ===== PLANS GRID ===== */}
      <div className="plans-container">
        <div className="plans-container-header">
          <div className="plans-container-title">
            <RocketOutlined /> {typeFilter === 'all' ? 'All' : planTypes[typeFilter]?.label} Plans
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="plans-empty">
            <div className="plans-empty-ic">👑</div>
            <h3>No plans found</h3>
            <p>Create a new premium plan</p>
            <button className="plans-btn-primary" onClick={() => setIsModalVisible(true)}>
              <PlusOutlined className="btn-icon" /> <span className="btn-text">Add Plan</span>
            </button>
          </div>
        ) : (
          <div className="plans-grid">
            {filteredPlans.map(plan => {
              const typeConfig = getTypeConfig(plan.plan_type);
              return (
                <div key={plan.id} className={`plans-card ${plan.is_popular ? 'popular' : ''}`}>
                  <div className={`plans-card-status ${plan.status}`}>
                    <span className="dot" /> {plan.status}
                  </div>
                  {plan.is_popular && (
                    <div className="plans-card-popular-badge"><StarFilled /> POPULAR</div>
                  )}

                  <div className="plans-card-type-badge" style={{ background: typeConfig.bg, color: typeConfig.color, border: `1.5px solid ${typeConfig.border}` }}>
                    {typeConfig.icon} {typeConfig.label}
                  </div>

                  <div className="plans-card-icon"><CrownOutlined /></div>
                  <h3 className="plans-card-name">{plan.plan_name}</h3>
                  <div className="plans-card-duration"><CalendarOutlined /> {getDurationLabel(plan.duration)}</div>
                  <div className="plans-card-price">
                    <span className="plans-card-price-currency">₹</span>
                    <span className="plans-card-price-value">{plan.price?.toLocaleString('en-IN')}</span>
                  </div>

                  {plan.plan_type === 'contact_view' && plan.contact_view_limit > 0 && (
                    <div className="plans-card-spec contact">📱 {plan.contact_view_limit >= 9999 ? 'Unlimited' : plan.contact_view_limit} Contact Views</div>
                  )}
                  {plan.plan_type === 'specific_user' && plan.profile_view_limit > 0 && (
                    <div className="plans-card-spec user">👤 {plan.profile_view_limit} Profile Views</div>
                  )}
                  {plan.plan_type === 'profile_boost' && (
                    <div className="plans-card-spec boost">🚀 {plan.boost_position === 'top' ? 'Top Position' : plan.boost_position === 'featured' ? 'Featured' : 'Highlighted'}</div>
                  )}

                  <div className="plans-card-features"><p>{plan.features}</p></div>

                  <div className="plans-card-actions">
                    <Tooltip title="View">
                      <button className="plans-card-btn view" onClick={() => handleView(plan)}><EyeOutlined /> <span>View</span></button>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <button className="plans-card-btn edit" onClick={() => handleEdit(plan)}><EditOutlined /> <span>Edit</span></button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <button className="plans-card-btn delete" onClick={() => handleDelete(plan.id)}><DeleteOutlined /></button>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== ADD MODAL ===== */}
      <Modal
        title={
          <div className="plans-modal-title">
            <div className="plans-modal-ic"><PlusOutlined /></div>
            <div>
              <div className="plans-modal-h">Add New Plan</div>
              <div className="plans-modal-sh">Create premium subscription plan</div>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
        footer={null}
        width={640}
        className="plans-modal"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ duration: 30, status: 'active', is_popular: false, plan_type: 'contact_view', contact_view_limit: 5, profile_view_limit: 10, boost_position: 'top' }}>
          
          <div style={{ marginBottom: 20 }}>
            <p className="plan-type-title">Select Plan Type</p>
            <div className="plan-type-grid">
              {Object.entries(planTypes).map(([key, config]) => (
                <div key={key} onClick={() => { setSelectedPlanType(key); form.setFieldsValue({ plan_type: key }); }}
                  className={`plan-type-card ${selectedPlanType === key ? 'active' : ''}`}
                  style={selectedPlanType === key ? { borderColor: config.color, background: config.bg } : {}}
                >
                  <div className="plan-type-icon">{config.icon}</div>
                  <div className="plan-type-label" style={{ color: selectedPlanType === key ? config.color : '#1e293b' }}>{config.label}</div>
                  <div className="plan-type-desc">{config.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <Form.Item name="plan_type" hidden><Input /></Form.Item>
          <Form.Item label="Plan Name" name="plan_name" rules={[{ required: true, message: 'Required' }]}>
            <Input prefix={<TagOutlined />} placeholder="e.g., Gold Contact Pack" />
          </Form.Item>

          <div className="plan-form-grid">
            <Form.Item label="Price (₹)" name="price" rules={[{ required: true, message: 'Required' }]}>
              <Input type="number" prefix="₹" placeholder="999" min={0} />
            </Form.Item>
            <Form.Item label="Duration" name="duration" rules={[{ required: true, message: 'Required' }]}>
              <Select placeholder="Select duration">
                <Option value={7}>7 Days</Option>
                <Option value={15}>15 Days</Option>
                <Option value={30}>1 Month</Option>
                <Option value={60}>2 Months</Option>
                <Option value={90}>3 Months</Option>
                <Option value={180}>6 Months</Option>
                <Option value={365}>1 Year</Option>
              </Select>
            </Form.Item>
          </div>

          {renderTypeFields(selectedPlanType)}

          <Form.Item label="Features" name="features" rules={[{ required: true, message: 'Required' }]}>
            <TextArea rows={3} placeholder="List features separated by commas..." />
          </Form.Item>

          <div className="plan-form-grid">
            <Form.Item label="Status" name="status">
              <Select>
                <Option value="active">🟢 Active</Option>
                <Option value="inactive">🔴 Inactive</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Popular" name="is_popular" valuePropName="checked">
              <Switch checkedChildren="Popular" unCheckedChildren="Standard" />
            </Form.Item>
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button type="primary" htmlType="submit" loading={loading} block icon={<PlusOutlined />}>
              Add Premium Plan
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ===== EDIT MODAL ===== */}
      <Modal
        title={
          <div className="plans-modal-title">
            <div className="plans-modal-ic edit"><EditOutlined /></div>
            <div>
              <div className="plans-modal-h">Edit Plan</div>
              <div className="plans-modal-sh">Update plan details</div>
            </div>
          </div>
        }
        open={isEditModalVisible}
        onCancel={() => { setIsEditModalVisible(false); setEditingPlan(null); }}
        footer={null}
        width={640}
        className="plans-modal"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <div className="plans-filters-wrapper" style={{ marginBottom: 16 }}>
            {Object.entries(planTypes).map(([key, config]) => (
              <div key={key} onClick={() => setEditPlanType(key)}
                className="plans-filter-btn"
                style={{
                  border: editPlanType === key ? `2px solid ${config.color}` : '1.5px solid #e2e8f0',
                  background: editPlanType === key ? config.bg : '#fff',
                  color: editPlanType === key ? config.color : '#94a3b8',
                }}
              >
                {config.icon} {config.label}
              </div>
            ))}
          </div>

          <Form.Item label="Plan Name" name="plan_name" rules={[{ required: true }]}>
            <Input prefix={<TagOutlined />} />
          </Form.Item>

          <div className="plan-form-grid">
            <Form.Item label="Price (₹)" name="price" rules={[{ required: true }]}>
              <Input type="number" prefix="₹" min={0} />
            </Form.Item>
            <Form.Item label="Duration" name="duration" rules={[{ required: true }]}>
              <Select>
                <Option value={7}>7 Days</Option>
                <Option value={15}>15 Days</Option>
                <Option value={30}>1 Month</Option>
                <Option value={60}>2 Months</Option>
                <Option value={90}>3 Months</Option>
                <Option value={180}>6 Months</Option>
                <Option value={365}>1 Year</Option>
              </Select>
            </Form.Item>
          </div>

          {renderTypeFields(editPlanType, true)}

          <Form.Item label="Features" name="features" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>

          <div className="plan-form-grid">
            <Form.Item label="Status" name="status">
              <Select>
                <Option value="active">🟢 Active</Option>
                <Option value="inactive">🔴 Inactive</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Popular" name="is_popular" valuePropName="checked">
              <Switch checkedChildren="Popular" unCheckedChildren="Standard" />
            </Form.Item>
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button type="primary" htmlType="submit" loading={loading} block icon={<EditOutlined />}>
              Update Plan
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ===== VIEW MODAL ===== */}
      <Modal
        title={
          <div className="plans-modal-title">
            <div className="plans-modal-ic view"><EyeOutlined /></div>
            <div>
              <div className="plans-modal-h">Plan Details</div>
              <div className="plans-modal-sh">Complete information</div>
            </div>
          </div>
        }
        open={viewModalVisible}
        onCancel={() => { setViewModalVisible(false); setSelectedPlan(null); }}
        footer={[<Button key="close" onClick={() => setViewModalVisible(false)}>Close</Button>]}
        width={500}
        className="plans-modal"
      >
        {selectedPlan && (() => {
          const typeConfig = getTypeConfig(selectedPlan.plan_type);
          return (
            <div className="plans-view-details">
              <div className="plans-view-icon-wrap">
                <div className="plans-view-icon"><CrownOutlined /></div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 14px', background: typeConfig.bg, color: typeConfig.color, border: `1.5px solid ${typeConfig.border}`, borderRadius: 999, fontSize: 12, fontWeight: 700, margin: '0 auto 12px', display: 'flex', width: 'max-content' }}>
                {typeConfig.icon} {typeConfig.label}
              </div>
              <h3 className="plans-view-name">{selectedPlan.plan_name}</h3>
              <div className="plans-view-price">₹{selectedPlan.price?.toLocaleString('en-IN')}</div>
              <div className="plans-view-info">
                <div className="plans-view-info-item">
                  <div className="plans-view-info-label">Duration</div>
                  <div className="plans-view-info-value">{getDurationLabel(selectedPlan.duration)}</div>
                </div>
                <div className="plans-view-info-item">
                  <div className="plans-view-info-label">Status</div>
                  <div className="plans-view-info-value" style={{ color: selectedPlan.status === 'active' ? '#10b981' : '#ef4444' }}>
                    {selectedPlan.status === 'active' ? '🟢 Active' : '🔴 Inactive'}
                  </div>
                </div>
                {selectedPlan.plan_type === 'contact_view' && (
                  <div className="plans-view-info-item">
                    <div className="plans-view-info-label">Contact Limit</div>
                    <div className="plans-view-info-value" style={{ color: '#3b82f6' }}>
                      📱 {selectedPlan.contact_view_limit >= 9999 ? 'Unlimited' : selectedPlan.contact_view_limit} Contacts
                    </div>
                  </div>
                )}
                {selectedPlan.plan_type === 'specific_user' && (
                  <div className="plans-view-info-item">
                    <div className="plans-view-info-label">Profile Limit</div>
                    <div className="plans-view-info-value" style={{ color: '#8b5cf6' }}>
                      👤 {selectedPlan.profile_view_limit} Profiles
                    </div>
                  </div>
                )}
                {selectedPlan.plan_type === 'profile_boost' && (
                  <div className="plans-view-info-item">
                    <div className="plans-view-info-label">Boost Position</div>
                    <div className="plans-view-info-value" style={{ color: '#f59e0b' }}>
                      🚀 {selectedPlan.boost_position === 'top' ? 'Top Position' : selectedPlan.boost_position === 'featured' ? 'Featured' : 'Highlighted'}
                    </div>
                  </div>
                )}
                <div className="plans-view-info-item">
                  <div className="plans-view-info-label">Type</div>
                  <div className="plans-view-info-value">{selectedPlan.is_popular ? '⭐ Popular' : '📌 Standard'}</div>
                </div>
              </div>
              <div className="plans-view-features-box">
                <div className="plans-view-features-label">✨ Features</div>
                <p className="plans-view-features-text">{selectedPlan.features}</p>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default AddPremiumPlan;